import { computed } from 'vue';
import { /* camelCase, */ get, startCase, capitalize } from 'lodash-es';
import { generate as generateParser } from 'peggy';
import grammar from './formula.peggy?raw';
// import { useCustomFields } from '@/utils/custom-fields';
// import { CustomFieldType } from '@/models/organization.models';
import { organizationData } from '@/store/helpers';

export type CalculatedIdentifierDef = {
  name: string;
  /** will be defined for variables */
  humanReadableName?: string;
  /** add whatever metadata; for now this will store custom field id for dynamic variables */
  // metadata?: string;
  identifierType: 'function' | 'variable';
  /** will be defined for variables */
  returnType?: 'string' | 'number' | 'date' | 'datetime' | 'boolean';
  /** will be defined for functions */
  exampleUsage?: string;
  exampleResult: string;
};
const VARIABLE_WRAPPER = {
  OPEN: '${',
  CLOSE: '}',
} as const;
export const VAR_CUSTOM_FIELD_PREFIX = 'item_field_';

type VExpression = VBinaryExpression | VUnaryExpression;
type VBinaryExpression = {
  type: 'expression';
  subType: 'binary';
  value: {
    left: VExpressionValue;
    right: { operator: VOperator; value: VExpressionValue }[];
  };
};
type VUnaryExpression = {
  type: 'expression';
  subType: 'unary';
  value: VExpressionValue;
};
type VExpressionValue =
  | VNumber
  | VString
  | VVariable
  | VFunctionCall
  | VExpression;
type VFunctionCall = { type: 'function-call'; value: string; args: VArguments };
type VArguments = { type: 'arguments'; value: VExpression[] };
type VOperator = { type: 'operator'; value: string };
type VVariable = { type: 'variable'; value: string };
type VString = { type: 'value'; subType: 'string'; value: string };
type VNumber = { type: 'value'; subType: 'number'; value: number };

/** Walks the expression tree and calls the callback for each node */
export const walkExpression = (
  expr: VExpressionValue,
  cb: (val: VExpressionValue) => void
) => {
  cb(expr);
  if (expr.type === 'expression') {
    if (expr['subType'] === 'binary') {
      walkExpression(expr.value.left, cb);
      expr.value.right.forEach((r) => {
        walkExpression(r.value, cb);
      });
    } else {
      walkExpression(expr.value, cb);
    }
  } else if (expr.type === 'function-call') {
    expr.args.value.forEach((arg) => {
      walkExpression(arg, cb);
    });
  }
};

const VARIABLE_UX_METADATA_MAP: Record<
  string,
  {
    humanReadableName: string;
    exampleResult: string;
  }
> = Object.freeze({
  itemLane: { humanReadableName: 'Lane', exampleResult: 'In progress' },
  itemEstimate: { humanReadableName: 'Estimate', exampleResult: '6' },
  itemLikelyEstimate: {
    humanReadableName: 'Likely estimate',
    exampleResult: '6',
  },
  itemOptimisticEstimate: {
    humanReadableName: 'Optimistic estimate',
    exampleResult: '6',
  },
  itemPessimisticEstimate: {
    humanReadableName: 'Pessimistic estimate',
    exampleResult: '6',
  },
  itemTimeEstimate: {
    humanReadableName: 'Time estimate',
    exampleResult: '17.6',
  },
  itemTimeSpentSeconds: {
    humanReadableName: 'Time spent (seconds)',
    exampleResult: '86400',
  },
  itemTeam: { humanReadableName: 'Team', exampleResult: 'Engineering' },
  itemColor: { humanReadableName: 'Color', exampleResult: 'Blue' },
  itemCycle: { humanReadableName: 'Cycle', exampleResult: 'Sprint 9.3' },
  itemTitle: {
    humanReadableName: 'Title',
    exampleResult: 'Implement Media Embedding Support',
  },
  itemNumber: { humanReadableName: 'Item number', exampleResult: 'I2498' },
  itemBlocker: {
    humanReadableName: 'Blocker',
    exampleResult: 'Blocked on review',
  },
  itemDueDate: { humanReadableName: 'Due date', exampleResult: '2022-08-01' },
  itemAssignee: { humanReadableName: 'Assignee', exampleResult: 'John Doe' },
  itemPriority: { humanReadableName: 'Priority', exampleResult: 'Critical' },
  itemStartDate: {
    humanReadableName: 'Start Date',
    exampleResult: '2022-08-01',
  },
  recipientName: {
    humanReadableName: 'Recipient name',
    exampleResult: 'John Doe',
  },
  itemParentTitle: {
    humanReadableName: 'Parent title',
    exampleResult: 'Epic: Implement Media Embedding Support',
  },
  itemPointEffort: { humanReadableName: 'Point effort', exampleResult: '6' },
  itemAuthorUsername: {
    humanReadableName: 'Author username',
    exampleResult: 'john.doe',
  },
});

export const getHumanReadableNameForVariable = (name: string) => {
  if (VARIABLE_UX_METADATA_MAP[name]) {
    return VARIABLE_UX_METADATA_MAP[name].humanReadableName;
  }
  let ret = name;

  const PREFIX = 'item';
  if (ret.startsWith(PREFIX) && ret.length > PREFIX.length)
    ret = ret.slice(PREFIX.length);
  ret = capitalize(startCase(ret));
  return ret;
};

export const replaceVariables = (
  str: string,
  replacement: Record<string, string> | 'preset:javascript'
) => {
  // regex expression string
  const esc = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regexp =
    `(${esc(VARIABLE_WRAPPER.OPEN)})` +
    `([^${esc(VARIABLE_WRAPPER.CLOSE)}]+)` +
    `(${esc(VARIABLE_WRAPPER.CLOSE)})`;

  // replace all occurrences of variables
  let ret = str;

  if (replacement === 'preset:javascript') {
    ret = ret.replace(new RegExp(regexp, 'g'), '$2'); // remove the variable wrapper
  } else {
    ret = ret.replace(new RegExp(regexp, 'g'), (match, _, g2) => {
      return replacement[g2] || match; // replace with the value or keep the original
    });
  }
  return ret;
};

export const useCalculatedCustomFieldUtils = function () {
  const { organization } = organizationData();

  /* ------------------------- identifiers / suggestions ------------------------- */

  const availableVariables = computed<CalculatedIdentifierDef[]>(() => {
    // const staticVars: CalculatedIdentifierDef[] = [
    //   {
    //     identifierType: 'variable',
    //     name: 'itemNumber',
    //     humanReadableName: 'Item number',
    //     returnType: 'string',
    //     exampleResult: 'I2498',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemTitle',
    //     humanReadableName: 'Item title',
    //     returnType: 'string',
    //     exampleResult: 'Implement Media Embedding Support',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemTypeLabel',
    //     humanReadableName: 'Item type',
    //     returnType: 'string',
    //     exampleResult: 'Defect',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemSubtaskCount',
    //     humanReadableName: 'Subtask count',
    //     returnType: 'number',
    //     exampleResult: '4',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemTeamLabel',
    //     humanReadableName: 'Team label',
    //     returnType: 'string',
    //     exampleResult: 'Engineering',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemDependencyCount',
    //     humanReadableName: 'Dependency count',
    //     returnType: 'number',
    //     exampleResult: '8',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemStatus',
    //     humanReadableName: 'Status',
    //     returnType: 'string',
    //     exampleResult: 'In Progress',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemCycleOrBoardLabel',
    //     humanReadableName: 'Cycle / Board',
    //     returnType: 'string',
    //     exampleResult: 'Sprint 9.3',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemStartDate',
    //     humanReadableName: 'Start date',
    //     returnType: 'date',
    //     exampleResult: '2022-08-01',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemEndDate',
    //     humanReadableName: 'End date',
    //     returnType: 'date',
    //     exampleResult: '2022-08-01',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemPointEstimate',
    //     humanReadableName: 'Point estimate',
    //     returnType: 'number',
    //     exampleResult: '6',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemTimeEstimate',
    //     humanReadableName: 'Time estimate',
    //     returnType: 'number',
    //     exampleResult: '17.6',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemTimeSpentSeconds',
    //     humanReadableName: 'Time spent (seconds)',
    //     returnType: 'number',
    //     exampleResult: '957',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemPriorityLabel',
    //     humanReadableName: 'Priority',
    //     returnType: 'string',
    //     exampleResult: 'Critical',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemTagCount',
    //     humanReadableName: 'Tag count',
    //     returnType: 'number',
    //     exampleResult: '3',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemIsBlocked',
    //     humanReadableName: 'Is blocked',
    //     returnType: 'boolean',
    //     exampleResult: 'true',
    //   },
    //   {
    //     identifierType: 'variable',
    //     name: 'itemBlockedReason',
    //     humanReadableName: 'Blocked reason',
    //     returnType: 'string',
    //     exampleResult: 'Blocked on review',
    //   },
    // ];
    // const dynamicVars: CalculatedIdentifierDef[] = (customFields.value || [])
    //   .filter((cf) => {
    //     const VALID_TYPES = [
    //       CustomFieldType.TEXT,
    //       CustomFieldType.NUMBER,
    //       CustomFieldType.BOOLEAN,
    //       CustomFieldType.DATE,
    //       CustomFieldType.CHOICE,
    //     ];
    //     if (VALID_TYPES.includes(cf.type) && !cf.isMultiSelect) {
    //       return true;
    //     }
    //     return false;
    //   })
    //   .map((cf) => {
    //     const returnType = (() => {
    //       if (cf.type === CustomFieldType.TEXT) return 'string';
    //       if (cf.type === CustomFieldType.NUMBER) return 'number';
    //       if (cf.type === CustomFieldType.BOOLEAN) return 'boolean';
    //       if (cf.type === CustomFieldType.DATE) return 'date';
    //       if (cf.type === CustomFieldType.CHOICE) return 'string';
    //       throw new Error('Unsupported type');
    //     })() as 'string' | 'number' | 'boolean' | 'date';
    //     return {
    //       identifierType: 'variable',
    //       name: `${VAR_CUSTOM_FIELD_PREFIX}${camelCase(cf.name)}`,
    //       humanReadableName: cf.name,
    //       metadata: cf.id,
    //       returnType,
    //       exampleResult: '',
    //     };
    //   });
    // return [...dynamicVars, ...staticVars];
    if (!organization.value) return [];

    return Object.entries(organization.value?.variables || {}).map(
      ([name, v]) => {
        const humanReadableName = getHumanReadableNameForVariable(name);
        const ret: CalculatedIdentifierDef = {
          identifierType: 'variable',
          name,
          humanReadableName,
          returnType: v.type,
          exampleResult: VARIABLE_UX_METADATA_MAP[name]?.exampleResult || '',
        };
        return ret;
      }
    );
  });
  const availableVariablesMap = computed(() => {
    return new Map<CalculatedIdentifierDef['name'], CalculatedIdentifierDef>(
      availableVariables.value.map((v) => [v.name, v])
    );
  });

  const availableFunctions: CalculatedIdentifierDef[] = [
    {
      identifierType: 'function',
      name: 'DATE',
      exampleUsage: 'DATE(2008, 7, 8)',
      exampleResult: 'Tue Jul 08 2008 00:00:00 GMT-0700 (PDT)',
    },
    {
      identifierType: 'function',
      name: 'DATEVALUE',
      exampleUsage: "DATEVALUE('8/22/2011')",
      exampleResult: 'Mon Aug 22 2011 00:00:00 GMT-0700 (PDT)',
    },
    {
      identifierType: 'function',
      name: 'DAY',
      exampleUsage: "DAY('15-Apr-11')",
      exampleResult: '15',
    },
    {
      identifierType: 'function',
      name: 'DAYS',
      exampleUsage: "DAYS('3/15/11', '2/1/11')",
      exampleResult: '42',
    },
    {
      identifierType: 'function',
      name: 'DAYS360',
      exampleUsage: "DAYS360('1-Jan-11', '31-Dec-11')",
      exampleResult: '360',
    },
    {
      identifierType: 'function',
      name: 'EDATE',
      exampleUsage: "EDATE('1/15/11', -1)",
      exampleResult: 'Wed Dec 15 2010 00:00:00 GMT-0800 (PST)',
    },
    {
      identifierType: 'function',
      name: 'EOMONTH',
      exampleUsage: "EOMONTH('1/1/11', -3)",
      exampleResult: 'Sun Oct 31 2010 00:00:00 GMT-0700 (PDT)',
    },
    {
      identifierType: 'function',
      name: 'HOUR',
      exampleUsage: "HOUR('7/18/2011 7:45:00 AM')",
      exampleResult: '7',
    },
    {
      identifierType: 'function',
      name: 'MINUTE',
      exampleUsage: "MINUTE('2/1/2011 12:45:00 PM')",
      exampleResult: '45',
    },
    {
      identifierType: 'function',
      name: 'ISOWEEKNUM',
      exampleUsage: "ISOWEEKNUM('3/9/2012')",
      exampleResult: '10',
    },
    {
      identifierType: 'function',
      name: 'MONTH',
      exampleUsage: "MONTH('15-Apr-11')",
      exampleResult: '4',
    },
    {
      identifierType: 'function',
      name: 'NETWORKDAYS',
      exampleUsage: "NETWORKDAYS('10/1/2012', '3/1/2013', ['11/22/2012'])",
      exampleResult: '109',
    },
    {
      identifierType: 'function',
      name: 'NETWORKDAYSINTL',
      exampleUsage: "NETWORKDAYSINTL('1/1/2006', '2/1/2006', 7, ['1/2/2006'])",
      exampleResult: '23',
    },
    {
      identifierType: 'function',
      name: 'NOW',
      exampleUsage: 'NOW()',
      exampleResult:
        'Thu Feb 20 2020 23:02:55 GMT+0100 (Central European Standard Time)',
    },
    {
      identifierType: 'function',
      name: 'SECOND',
      exampleUsage: "SECOND('2/1/2011 4:48:18 PM')",
      exampleResult: '18',
    },
    {
      identifierType: 'function',
      name: 'TIME',
      exampleUsage: 'TIME(16, 48, 10)',
      exampleResult: '0.7001157407407408',
    },
    {
      identifierType: 'function',
      name: 'TIMEVALUE',
      exampleUsage: "TIMEVALUE('22-Aug-2011 6:35 AM')",
      exampleResult: '0.2743055555555556',
    },
    {
      identifierType: 'function',
      name: 'TODAY',
      exampleUsage: 'TODAY()',
      exampleResult:
        'Thu Feb 20 2020 23:02:55 GMT+0100 (Central European Standard Time)',
    },
    {
      identifierType: 'function',
      name: 'WEEKDAY',
      exampleUsage: "WEEKDAY('2/14/2008', 3)",
      exampleResult: '3',
    },
    {
      identifierType: 'function',
      name: 'YEAR',
      exampleUsage: "YEAR('7/5/2008')",
      exampleResult: '2008',
    },
    {
      identifierType: 'function',
      name: 'WEEKNUM',
      exampleUsage: "WEEKNUM('3/9/2012', 2)",
      exampleResult: '11',
    },
    {
      identifierType: 'function',
      name: 'WORKDAY',
      exampleUsage: "WORKDAY('10/1/2008', 151, ['11/26/2008', '12/4/2008'])",
      exampleResult: 'Mon May 04 2009 00:00:00 GMT-0700 (PDT)',
    },
    {
      identifierType: 'function',
      name: 'WORKDAYINTL',
      exampleUsage: "WORKDAYINTL('1/1/2012', 30, 17)",
      exampleResult: 'Sun Feb 05 2012 00:00:00 GMT-0800 (PST)',
    },
    {
      identifierType: 'function',
      name: 'YEARFRAC',
      exampleUsage: "YEARFRAC('1/1/2012', '7/30/2012', 3)",
      exampleResult: '0.5780821917808219',
    },
    {
      identifierType: 'function',
      name: 'ACCRINT',
      exampleUsage:
        "ACCRINT('01/01/2011', '02/01/2011', '07/01/2014', 0.1, 1000, 1, 0)",
      exampleResult: '350',
    },
    {
      identifierType: 'function',
      name: 'CUMIPMT',
      exampleUsage: 'CUMIPMT(0.1/12, 30*12, 100000, 13, 24, 0)',
      exampleResult: '-9916.77251395708',
    },
    {
      identifierType: 'function',
      name: 'CUMPRINC',
      exampleUsage: 'CUMPRINC(0.1/12, 30*12, 100000, 13, 24, 0)',
      exampleResult: '-614.0863271085149',
    },
    {
      identifierType: 'function',
      name: 'DB',
      exampleUsage: 'DB(1000000, 100000, 6, 1, 6)',
      exampleResult: '159500',
    },
    {
      identifierType: 'function',
      name: 'DDB',
      exampleUsage: 'DDB(1000000, 100000, 6, 1, 1.5)',
      exampleResult: '250000',
    },
    {
      identifierType: 'function',
      name: 'DOLLARDE',
      exampleUsage: 'DOLLARDE(1.1, 16)',
      exampleResult: '1.625',
    },
    {
      identifierType: 'function',
      name: 'DOLLARFR',
      exampleUsage: 'DOLLARFR(1.625, 16)',
      exampleResult: '1.1',
    },
    {
      identifierType: 'function',
      name: 'EFFECT',
      exampleUsage: 'EFFECT(0.1, 4)',
      exampleResult: '0.10381289062499977',
    },
    {
      identifierType: 'function',
      name: 'FV',
      exampleUsage: 'FV(0.1/12, 10, -100, -1000, 0)',
      exampleResult: '2124.874409194097',
    },
    {
      identifierType: 'function',
      name: 'FVSCHEDULE',
      exampleUsage: 'FVSCHEDULE(100, [0.09,0.1,0.11])',
      exampleResult: '133.08900000000003',
    },
    {
      identifierType: 'function',
      name: 'IPMT',
      exampleUsage: 'IPMT(0.1/12, 6, 2*12, 100000, 1000000, 0)',
      exampleResult: '928.8235718400465',
    },
    {
      identifierType: 'function',
      name: 'IRR',
      exampleUsage: 'IRR([-75000,12000,15000,18000,21000,24000], 0.075)',
      exampleResult: '0.05715142887178447',
    },
    {
      identifierType: 'function',
      name: 'ISPMT',
      exampleUsage: 'ISPMT(0.1/12, 6, 2*12, 100000)',
      exampleResult: '-625',
    },
    {
      identifierType: 'function',
      name: 'MIRR',
      exampleUsage: 'MIRR([-75000,12000,15000,18000,21000,24000], 0.1, 0.12)',
      exampleResult: '0.07971710360838036',
    },
    {
      identifierType: 'function',
      name: 'NOMINAL',
      exampleUsage: 'NOMINAL(0.1, 4)',
      exampleResult: '0.09645475633778045',
    },
    {
      identifierType: 'function',
      name: 'NPER',
      exampleUsage: 'NPER(0.1/12, -100, -1000, 10000, 0)',
      exampleResult: '63.39385422740764',
    },
    {
      identifierType: 'function',
      name: 'NPV',
      exampleUsage: 'NPV(0.1, -10000, 2000, 4000, 8000)',
      exampleResult: '1031.3503176012546',
    },
    {
      identifierType: 'function',
      name: 'PDURATION',
      exampleUsage: 'PDURATION(0.1, 1000, 2000)',
      exampleResult: '7.272540897341714',
    },
    {
      identifierType: 'function',
      name: 'PMT',
      exampleUsage: 'PMT(0.1/12, 2*12, 100000, 1000000, 0)',
      exampleResult: '-42426.08563793503',
    },
    {
      identifierType: 'function',
      name: 'PPMT',
      exampleUsage: 'PPMT(0.1/12, 6, 2*12, 100000, 1000000, 0)',
      exampleResult: '-43354.909209775076',
    },
    {
      identifierType: 'function',
      name: 'PV',
      exampleUsage: 'PV(0.1/12, 2*12, 1000, 10000, 0)',
      exampleResult: '-29864.950264779152',
    },
    {
      identifierType: 'function',
      name: 'RATE',
      exampleUsage: 'RATE(2*12, -1000, -10000, 100000, 0, 0.1)',
      exampleResult: '0.06517891177181533',
    },
    {
      identifierType: 'function',
      name: 'BIN2DEC',
      exampleUsage: 'BIN2DEC(101010)',
      exampleResult: '42',
    },
    {
      identifierType: 'function',
      name: 'BIN2HEX',
      exampleUsage: 'BIN2HEX(101010)',
      exampleResult: '2a',
    },
    {
      identifierType: 'function',
      name: 'BIN2OCT',
      exampleUsage: 'BIN2OCT(101010)',
      exampleResult: '52',
    },
    {
      identifierType: 'function',
      name: 'BITAND',
      exampleUsage: 'BITAND(42, 24)',
      exampleResult: '8',
    },
    {
      identifierType: 'function',
      name: 'BITLSHIFT',
      exampleUsage: 'BITLSHIFT(42, 24)',
      exampleResult: '704643072',
    },
    {
      identifierType: 'function',
      name: 'BITOR',
      exampleUsage: 'BITOR(42, 24)',
      exampleResult: '58',
    },
    {
      identifierType: 'function',
      name: 'BITRSHIFT',
      exampleUsage: 'BITRSHIFT(42, 2)',
      exampleResult: '10',
    },
    {
      identifierType: 'function',
      name: 'BITXOR',
      exampleUsage: 'BITXOR(42, 24)',
      exampleResult: '50',
    },
    {
      identifierType: 'function',
      name: 'COMPLEX',
      exampleUsage: 'COMPLEX(3, 4)',
      exampleResult: '3+4i',
    },
    {
      identifierType: 'function',
      name: 'CONVERT',
      exampleUsage: "CONVERT(64, 'kibyte', 'bit')",
      exampleResult: '524288',
    },
    {
      identifierType: 'function',
      name: 'DEC2BIN',
      exampleUsage: 'DEC2BIN(42)',
      exampleResult: '101010',
    },
    {
      identifierType: 'function',
      name: 'DEC2HEX',
      exampleUsage: 'DEC2HEX(42)',
      exampleResult: '2a',
    },
    {
      identifierType: 'function',
      name: 'DEC2OCT',
      exampleUsage: 'DEC2OCT(42)',
      exampleResult: '52',
    },
    {
      identifierType: 'function',
      name: 'DELTA',
      exampleUsage: 'DELTA(42, 42)',
      exampleResult: '1',
    },
    {
      identifierType: 'function',
      name: 'ERF',
      exampleUsage: 'ERF(1)',
      exampleResult: '0.8427007929497149',
    },
    {
      identifierType: 'function',
      name: 'ERFC',
      exampleUsage: 'ERFC(1)',
      exampleResult: '0.1572992070502851',
    },
    {
      identifierType: 'function',
      name: 'GESTEP',
      exampleUsage: 'GESTEP(42, 24)',
      exampleResult: '1',
    },
    {
      identifierType: 'function',
      name: 'HEX2BIN',
      exampleUsage: "HEX2BIN('2a')",
      exampleResult: '101010',
    },
    {
      identifierType: 'function',
      name: 'HEX2DEC',
      exampleUsage: "HEX2DEC('2a')",
      exampleResult: '42',
    },
    {
      identifierType: 'function',
      name: 'HEX2OCT',
      exampleUsage: "HEX2OCT('2a')",
      exampleResult: '52',
    },
    {
      identifierType: 'function',
      name: 'IMABS',
      exampleUsage: "IMABS('3+4i')",
      exampleResult: '5',
    },
    {
      identifierType: 'function',
      name: 'IMAGINARY',
      exampleUsage: "IMAGINARY('3+4i')",
      exampleResult: '4',
    },
    {
      identifierType: 'function',
      name: 'IMARGUMENT',
      exampleUsage: "IMARGUMENT('3+4i')",
      exampleResult: '0.9272952180016122',
    },
    {
      identifierType: 'function',
      name: 'IMCONJUGATE',
      exampleUsage: "IMCONJUGATE('3+4i')",
      exampleResult: '3-4i',
    },
    {
      identifierType: 'function',
      name: 'IMCOS',
      exampleUsage: "IMCOS('1+i')",
      exampleResult: '0.8337300251311491-0.9888977057628651i',
    },
    {
      identifierType: 'function',
      name: 'IMCOSH',
      exampleUsage: "IMCOSH('1+i')",
      exampleResult: '0.8337300251311491+0.9888977057628651i',
    },
    {
      identifierType: 'function',
      name: 'IMCOT',
      exampleUsage: "IMCOT('1+i')",
      exampleResult: '0.21762156185440265-0.8680141428959249i',
    },
    {
      identifierType: 'function',
      name: 'IMCSC',
      exampleUsage: "IMCSC('1+i')",
      exampleResult: '0.6215180171704283-0.3039310016284264i',
    },
    {
      identifierType: 'function',
      name: 'IMCSCH',
      exampleUsage: "IMCSCH('1+i')",
      exampleResult: '0.3039310016284264-0.6215180171704283i',
    },
    {
      identifierType: 'function',
      name: 'IMDIV',
      exampleUsage: "IMDIV('1+2i', '3+4i')",
      exampleResult: '0.44+0.08i',
    },
    {
      identifierType: 'function',
      name: 'IMEXP',
      exampleUsage: "IMEXP('1+i')",
      exampleResult: '1.4686939399158851+2.2873552871788423i',
    },
    {
      identifierType: 'function',
      name: 'IMLN',
      exampleUsage: "IMLN('1+i')",
      exampleResult: '0.3465735902799727+0.7853981633974483i',
    },
    {
      identifierType: 'function',
      name: 'IMLOG10',
      exampleUsage: "IMLOG10('1+i')",
      exampleResult: '0.1505149978319906+0.3410940884604603i',
    },
    {
      identifierType: 'function',
      name: 'IMLOG2',
      exampleUsage: "IMLOG2('1+i')",
      exampleResult: '0.5000000000000001+1.1330900354567985i',
    },
    {
      identifierType: 'function',
      name: 'IMPOWER',
      exampleUsage: "IMPOWER('1+i', 2)",
      exampleResult: '1.2246063538223775e-16+2.0000000000000004i',
    },
    {
      identifierType: 'function',
      name: 'IMPRODUCT',
      exampleUsage: "IMPRODUCT('1+2i', '3+4i', '5+6i')",
      exampleResult: '-85+20i',
    },
    {
      identifierType: 'function',
      name: 'IMREAL',
      exampleUsage: "IMREAL('3+4i')",
      exampleResult: '3',
    },
    {
      identifierType: 'function',
      name: 'IMSEC',
      exampleUsage: "IMSEC('1+i')",
      exampleResult: '0.4983370305551868+0.591083841721045i',
    },
    {
      identifierType: 'function',
      name: 'IMSECH',
      exampleUsage: "IMSECH('1+i')",
      exampleResult: '0.4983370305551868-0.591083841721045i',
    },
    {
      identifierType: 'function',
      name: 'IMSIN',
      exampleUsage: "IMSIN('1+i')",
      exampleResult: '1.2984575814159773+0.6349639147847361i',
    },
    {
      identifierType: 'function',
      name: 'IMSINH',
      exampleUsage: "IMSINH('1+i')",
      exampleResult: '0.6349639147847361+1.2984575814159773i',
    },
    {
      identifierType: 'function',
      name: 'IMSQRT',
      exampleUsage: "IMSQRT('1+i')",
      exampleResult: '1.0986841134678098+0.45508986056222733i',
    },
    {
      identifierType: 'function',
      name: 'IMSUB',
      exampleUsage: "IMSUB('3+4i', '1+2i')",
      exampleResult: '2+2i',
    },
    {
      identifierType: 'function',
      name: 'IMSUM',
      exampleUsage: "IMSUM('1+2i', '3+4i', '5+6i')",
      exampleResult: '9+12i',
    },
    {
      identifierType: 'function',
      name: 'IMTAN',
      exampleUsage: "IMTAN('1+i')",
      exampleResult: '0.2717525853195117+1.0839233273386946i',
    },
    {
      identifierType: 'function',
      name: 'OCT2BIN',
      exampleUsage: "OCT2BIN('52')",
      exampleResult: '101010',
    },
    {
      identifierType: 'function',
      name: 'OCT2DEC',
      exampleUsage: "OCT2DEC('52')",
      exampleResult: '42',
    },
    {
      identifierType: 'function',
      name: 'OCT2HEX',
      exampleUsage: "OCT2HEX('52')",
      exampleResult: '2a',
    },
    {
      identifierType: 'function',
      name: 'AND',
      exampleUsage: 'AND(true, false, true)',
      exampleResult: 'false',
    },
    {
      identifierType: 'function',
      name: 'false',
      exampleUsage: 'FALSE()',
      exampleResult: 'false',
    },
    {
      identifierType: 'function',
      name: 'IF',
      exampleUsage: "IF(true, 'Hello!', 'Goodbye!')",
      exampleResult: 'Hello!',
    },
    {
      identifierType: 'function',
      name: 'IFS',
      exampleUsage: "IFS(false, 'Hello!', true, 'Goodbye!')",
      exampleResult: 'Goodbye!',
    },
    {
      identifierType: 'function',
      name: 'IFERROR',
      exampleUsage: "IFERROR('#DIV/0!', 'Error')",
      exampleResult: 'Error',
    },
    {
      identifierType: 'function',
      name: 'IFNA',
      exampleUsage: "IFNA('#N/A', 'Error')",
      exampleResult: 'Error',
    },
    {
      identifierType: 'function',
      name: 'NOT',
      exampleUsage: 'NOT(true)',
      exampleResult: 'false',
    },
    {
      identifierType: 'function',
      name: 'OR',
      exampleUsage: 'OR(true, false, true)',
      exampleResult: 'true',
    },
    {
      identifierType: 'function',
      name: 'SWITCH',
      exampleUsage: "SWITCH(7, 9, 'Nine', 7, 'Seven')",
      exampleResult: 'Seven',
    },
    {
      identifierType: 'function',
      name: 'true',
      exampleUsage: 'TRUE()',
      exampleResult: 'true',
    },
    {
      identifierType: 'function',
      name: 'XOR',
      exampleUsage: 'XOR(true, false, true)',
      exampleResult: 'false',
    },
    {
      identifierType: 'function',
      name: 'ABS',
      exampleUsage: 'ABS(-4)',
      exampleResult: '4',
    },
    {
      identifierType: 'function',
      name: 'ACOS',
      exampleUsage: 'ACOS(-0.5)',
      exampleResult: '2.0943951023931957',
    },
    {
      identifierType: 'function',
      name: 'ACOSH',
      exampleUsage: 'ACOSH(10)',
      exampleResult: '2.993222846126381',
    },
    {
      identifierType: 'function',
      name: 'ACOT',
      exampleUsage: 'ACOT(2)',
      exampleResult: '0.46364760900080615',
    },
    {
      identifierType: 'function',
      name: 'ACOTH',
      exampleUsage: 'ACOTH(6)',
      exampleResult: '0.16823611831060645',
    },
    {
      identifierType: 'function',
      name: 'AGGREGATE',
      exampleUsage: "AGGREGATE(9, 4, [-5,15], [32,'Hello World!'])",
      exampleResult: '10,32',
    },
    {
      identifierType: 'function',
      name: 'ARABIC',
      exampleUsage: "ARABIC('MCMXII')",
      exampleResult: '1912',
    },
    {
      identifierType: 'function',
      name: 'ASIN',
      exampleUsage: 'ASIN(-0.5)',
      exampleResult: '-0.5235987755982988',
    },
    {
      identifierType: 'function',
      name: 'ASINH',
      exampleUsage: 'ASINH(-2.5)',
      exampleResult: '-1.6472311463710965',
    },
    {
      identifierType: 'function',
      name: 'ATAN',
      exampleUsage: 'ATAN(1)',
      exampleResult: '0.7853981633974483',
    },
    {
      identifierType: 'function',
      name: 'ATAN2',
      exampleUsage: 'ATAN2(-1, -1)',
      exampleResult: '-2.356194490192345',
    },
    {
      identifierType: 'function',
      name: 'ATANH',
      exampleUsage: 'ATANH(-0.1)',
      exampleResult: '-0.10033534773107562',
    },
    {
      identifierType: 'function',
      name: 'BASE',
      exampleUsage: 'BASE(15, 2, 10)',
      exampleResult: '0000001111',
    },
    {
      identifierType: 'function',
      name: 'CEILING',
      exampleUsage: 'CEILING(-5.5, 2, -1)',
      exampleResult: '-6',
    },
    {
      identifierType: 'function',
      name: 'CEILINGMATH',
      exampleUsage: 'CEILINGMATH(-5.5, 2, -1)',
      exampleResult: '-6',
    },
    {
      identifierType: 'function',
      name: 'CEILINGPRECISE',
      exampleUsage: 'CEILINGPRECISE(-4.1, -2)',
      exampleResult: '-4',
    },
    {
      identifierType: 'function',
      name: 'COMBIN',
      exampleUsage: 'COMBIN(8, 2)',
      exampleResult: '28',
    },
    {
      identifierType: 'function',
      name: 'COMBINA',
      exampleUsage: 'COMBINA(4, 3)',
      exampleResult: '20',
    },
    {
      identifierType: 'function',
      name: 'COS',
      exampleUsage: 'COS(1)',
      exampleResult: '0.5403023058681398',
    },
    {
      identifierType: 'function',
      name: 'COSH',
      exampleUsage: 'COSH(1)',
      exampleResult: '1.5430806348152437',
    },
    {
      identifierType: 'function',
      name: 'COT',
      exampleUsage: 'COT(30)',
      exampleResult: '-0.15611995216165922',
    },
    {
      identifierType: 'function',
      name: 'COTH',
      exampleUsage: 'COTH(2)',
      exampleResult: '1.0373147207275482',
    },
    {
      identifierType: 'function',
      name: 'CSC',
      exampleUsage: 'CSC(15)',
      exampleResult: '1.5377805615408537',
    },
    {
      identifierType: 'function',
      name: 'CSCH',
      exampleUsage: 'CSCH(1.5)',
      exampleResult: '0.46964244059522464',
    },
    {
      identifierType: 'function',
      name: 'DECIMAL',
      exampleUsage: "DECIMAL('FF', 16)",
      exampleResult: '255',
    },
    {
      identifierType: 'function',
      name: 'EVEN',
      exampleUsage: 'EVEN(-1)',
      exampleResult: '-2',
    },
    {
      identifierType: 'function',
      name: 'EXP',
      exampleUsage: 'EXP(1)',
      exampleResult: '2.718281828459045',
    },
    {
      identifierType: 'function',
      name: 'FACT',
      exampleUsage: 'FACT(5)',
      exampleResult: '120',
    },
    {
      identifierType: 'function',
      name: 'FACTDOUBLE',
      exampleUsage: 'FACTDOUBLE(7)',
      exampleResult: '105',
    },
    {
      identifierType: 'function',
      name: 'FLOOR',
      exampleUsage: 'FLOOR(-3.1)',
      exampleResult: '-4',
    },
    {
      identifierType: 'function',
      name: 'FLOORMATH',
      exampleUsage: 'FLOORMATH(-4.1, -2, -1)',
      exampleResult: '-4',
    },
    {
      identifierType: 'function',
      name: 'FLOORPRECISE',
      exampleUsage: 'FLOORPRECISE(-3.1, -2)',
      exampleResult: '-4',
    },
    {
      identifierType: 'function',
      name: 'GCD',
      exampleUsage: 'GCD(24, 36, 48)',
      exampleResult: '12',
    },
    {
      identifierType: 'function',
      name: 'INT',
      exampleUsage: 'INT(-8.9)',
      exampleResult: '-9',
    },
    {
      identifierType: 'function',
      name: 'ISEVEN',
      exampleUsage: 'ISEVEN(-2.5)',
      exampleResult: 'true',
    },
    {
      identifierType: 'function',
      name: 'ISOCEILING',
      exampleUsage: 'ISOCEILING(-4.1, -2)',
      exampleResult: '-4',
    },
    {
      identifierType: 'function',
      name: 'ISODD',
      exampleUsage: 'ISODD(-2.5)',
      exampleResult: 'false',
    },
    {
      identifierType: 'function',
      name: 'LCM',
      exampleUsage: 'LCM(24, 36, 48)',
      exampleResult: '144',
    },
    {
      identifierType: 'function',
      name: 'LN',
      exampleUsage: 'LN(86)',
      exampleResult: '4.454347296253507',
    },
    {
      identifierType: 'function',
      name: 'LOG',
      exampleUsage: 'LOG(8, 2)',
      exampleResult: '3',
    },
    {
      identifierType: 'function',
      name: 'LOG10',
      exampleUsage: 'LOG10(100000)',
      exampleResult: '5',
    },
    {
      identifierType: 'function',
      name: 'MOD',
      exampleUsage: 'MOD(3, -2)',
      exampleResult: '-1',
    },
    {
      identifierType: 'function',
      name: 'MROUND',
      exampleUsage: 'MROUND(-10, -3)',
      exampleResult: '-9',
    },
    {
      identifierType: 'function',
      name: 'MULTINOMIAL',
      exampleUsage: 'MULTINOMIAL(2, 3, 4)',
      exampleResult: '1260',
    },
    {
      identifierType: 'function',
      name: 'ODD',
      exampleUsage: 'ODD(-1.5)',
      exampleResult: '-3',
    },
    {
      identifierType: 'function',
      name: 'POWER',
      exampleUsage: 'POWER(5, 2)',
      exampleResult: '25',
    },
    {
      identifierType: 'function',
      name: 'PRODUCT',
      exampleUsage: 'PRODUCT(5, 15, 30)',
      exampleResult: '2250',
    },
    {
      identifierType: 'function',
      name: 'QUOTIENT',
      exampleUsage: 'QUOTIENT(-10, 3)',
      exampleResult: '-3',
    },
    {
      identifierType: 'function',
      name: 'RADIANS',
      exampleUsage: 'RADIANS(180)',
      exampleResult: '3.141592653589793',
    },
    {
      identifierType: 'function',
      name: 'RAND',
      exampleUsage: 'RAND()',
      exampleResult: '[Random real number greater between 0 and 1]',
    },
    {
      identifierType: 'function',
      name: 'RANDBETWEEN',
      exampleUsage: 'RANDBETWEEN(-1, 1)',
      exampleResult: '[Random integer between bottom and top]',
    },
    {
      identifierType: 'function',
      name: 'ROUND',
      exampleUsage: 'ROUND(626.3, -3)',
      exampleResult: '1000',
    },
    {
      identifierType: 'function',
      name: 'ROUNDDOWN',
      exampleUsage: 'ROUNDDOWN(-3.14159, 2)',
      exampleResult: '-3.14',
    },
    {
      identifierType: 'function',
      name: 'ROUNDUP',
      exampleUsage: 'ROUNDUP(-3.14159, 2)',
      exampleResult: '-3.15',
    },
    {
      identifierType: 'function',
      name: 'SEC',
      exampleUsage: 'SEC(45)',
      exampleResult: '1.9035944074044246',
    },
    {
      identifierType: 'function',
      name: 'SECH',
      exampleUsage: 'SECH(45)',
      exampleResult: '5.725037161098787e-20',
    },
    {
      identifierType: 'function',
      name: 'SIGN',
      exampleUsage: 'SIGN(-0.00001)',
      exampleResult: '-1',
    },
    {
      identifierType: 'function',
      name: 'SIN',
      exampleUsage: 'SIN(1)',
      exampleResult: '0.8414709848078965',
    },
    {
      identifierType: 'function',
      name: 'SINH',
      exampleUsage: 'SINH(1)',
      exampleResult: '1.1752011936438014',
    },
    {
      identifierType: 'function',
      name: 'SQRT',
      exampleUsage: 'SQRT(16)',
      exampleResult: '4',
    },
    {
      identifierType: 'function',
      name: 'SQRTPI',
      exampleUsage: 'SQRTPI(2)',
      exampleResult: '2.5066282746310002',
    },
    {
      identifierType: 'function',
      name: 'SUBTOTAL',
      exampleUsage: "SUBTOTAL(9, [-5,15], [32,'Hello World!'])",
      exampleResult: '10,32',
    },
    {
      identifierType: 'function',
      name: 'SUM',
      exampleUsage: "SUM(-5, 15, 32, 'Hello World!')",
      exampleResult: '42',
    },
    {
      identifierType: 'function',
      name: 'SUMIF',
      exampleUsage: "SUMIF([2,4,8,16], '>5')",
      exampleResult: '24',
    },
    {
      identifierType: 'function',
      name: 'SUMIFS',
      exampleUsage: "SUMIFS([2,4,8,16], [1,2,3,4], '>=2', [1,2,4,8], '<=4')",
      exampleResult: '12',
    },
    {
      identifierType: 'function',
      name: 'SUMPRODUCT',
      exampleUsage: 'SUMPRODUCT([[1,2],[3,4]], [[1,0],[0,1]])',
      exampleResult: '5',
    },
    {
      identifierType: 'function',
      name: 'SUMSQ',
      exampleUsage: 'SUMSQ(3, 4)',
      exampleResult: '25',
    },
    {
      identifierType: 'function',
      name: 'SUMX2MY2',
      exampleUsage: 'SUMX2MY2([1,2], [3,4])',
      exampleResult: '-20',
    },
    {
      identifierType: 'function',
      name: 'SUMX2PY2',
      exampleUsage: 'SUMX2PY2([1,2], [3,4])',
      exampleResult: '30',
    },
    {
      identifierType: 'function',
      name: 'SUMXMY2',
      exampleUsage: 'SUMXMY2([1,2], [3,4])',
      exampleResult: '8',
    },
    {
      identifierType: 'function',
      name: 'TAN',
      exampleUsage: 'TAN(1)',
      exampleResult: '1.5574077246549023',
    },
    {
      identifierType: 'function',
      name: 'TANH',
      exampleUsage: 'TANH(-2)',
      exampleResult: '-0.9640275800758168',
    },
    {
      identifierType: 'function',
      name: 'TRUNC',
      exampleUsage: 'TRUNC(-8.9)',
      exampleResult: '-8',
    },
    {
      identifierType: 'function',
      name: 'AVEDEV',
      exampleUsage: 'AVEDEV([2,4], [8,16])',
      exampleResult: '4.5',
    },
    {
      identifierType: 'function',
      name: 'AVERAGE',
      exampleUsage: 'AVERAGE([2,4], [8,16])',
      exampleResult: '7.5',
    },
    {
      identifierType: 'function',
      name: 'AVERAGEA',
      exampleUsage: 'AVERAGEA([2,4], [8,16])',
      exampleResult: '7.5',
    },
    {
      identifierType: 'function',
      name: 'AVERAGEIF',
      exampleUsage: "AVERAGEIF([2,4,8,16], '>5', [1, 2, 3, 4])",
      exampleResult: '3.5',
    },
    {
      identifierType: 'function',
      name: 'AVERAGEIFS',
      exampleUsage:
        "AVERAGEIFS([2,4,8,16], [1,2,3,4], '>=2', [1,2,4,8], '<=4')",
      exampleResult: '6',
    },
    {
      identifierType: 'function',
      name: 'BETADIST',
      exampleUsage: 'BETADIST(2, 8, 10, true, 1, 3)',
      exampleResult: '0.6854705810117458',
    },
    {
      identifierType: 'function',
      name: 'BETAINV',
      exampleUsage: 'BETAINV(0.6854705810117458, 8, 10, 1, 3)',
      exampleResult: '1.9999999999999998',
    },
    {
      identifierType: 'function',
      name: 'BINOMDIST',
      exampleUsage: 'BINOMDIST(6, 10, 0.5, false)',
      exampleResult: '0.205078125',
    },
    {
      identifierType: 'function',
      name: 'CORREL',
      exampleUsage: 'CORREL([3,2,4,5,6], [9,7,12,15,17])',
      exampleResult: '0.9970544855015815',
    },
    {
      identifierType: 'function',
      name: 'COUNT',
      exampleUsage: 'COUNT([1,2], [3,4])',
      exampleResult: '4',
    },
    {
      identifierType: 'function',
      name: 'COUNTA',
      exampleUsage: "COUNTA([1, null, 3, 'a', '', 'c'])",
      exampleResult: '4',
    },
    {
      identifierType: 'function',
      name: 'COUNTBLANK',
      exampleUsage: "COUNTBLANK([1, null, 3, 'a', '', 'c'])",
      exampleResult: '2',
    },
    {
      identifierType: 'function',
      name: 'COUNTIF',
      exampleUsage:
        "COUNTIF(['Caen', 'Melbourne', 'Palo Alto', 'Singapore'], 'a')",
      exampleResult: '3',
    },
    {
      identifierType: 'function',
      name: 'COUNTIFS',
      exampleUsage: "COUNTIFS([2,4,8,16], [1,2,3,4], '>=2', [1,2,4,8], '<=4')",
      exampleResult: '2',
    },
    {
      identifierType: 'function',
      name: 'COUNTUNIQUE',
      exampleUsage: 'COUNTUNIQUE([1,1,2,2,3,3])',
      exampleResult: '3',
    },
    {
      identifierType: 'function',
      name: 'COVARIANCEP',
      exampleUsage: 'COVARIANCEP([3,2,4,5,6], [9,7,12,15,17])',
      exampleResult: '5.2',
    },
    {
      identifierType: 'function',
      name: 'COVARIANCES',
      exampleUsage: 'COVARIANCES([2,4,8], [5,11,12])',
      exampleResult: '9.666666666666668',
    },
    {
      identifierType: 'function',
      name: 'DEVSQ',
      exampleUsage: 'DEVSQ([2,4,8,16])',
      exampleResult: '115',
    },
    {
      identifierType: 'function',
      name: 'EXPONDIST',
      exampleUsage: 'EXPONDIST(0.2, 10, true)',
      exampleResult: '0.8646647167633873',
    },
    {
      identifierType: 'function',
      name: 'FDIST',
      exampleUsage: 'FDIST(15.2069, 6, 4, false)',
      exampleResult: '0.0012237917087831735',
    },
    {
      identifierType: 'function',
      name: 'FINV',
      exampleUsage: 'FINV(0.01, 6, 4)',
      exampleResult: '0.10930991412457851',
    },
    {
      identifierType: 'function',
      name: 'FISHER',
      exampleUsage: 'FISHER(0.75)',
      exampleResult: '0.9729550745276566',
    },
    {
      identifierType: 'function',
      name: 'FISHERINV',
      exampleUsage: 'FISHERINV(0.9729550745276566)',
      exampleResult: '0.75',
    },
    {
      identifierType: 'function',
      name: 'FORECAST',
      exampleUsage: 'FORECAST(30, [6,7,9,15,21], [20,28,31,38,40])',
      exampleResult: '10.607253086419755',
    },
    {
      identifierType: 'function',
      name: 'FREQUENCY',
      exampleUsage: 'FREQUENCY([79,85,78,85,50,81,95,88,97], [70,79,89])',
      exampleResult: '1,2,4,2',
    },
    {
      identifierType: 'function',
      name: 'GAMMA',
      exampleUsage: 'GAMMA(2.5)',
      exampleResult: '1.3293403919101043',
    },
    {
      identifierType: 'function',
      name: 'GAMMALN',
      exampleUsage: 'GAMMALN(10)',
      exampleResult: '12.801827480081961',
    },
    {
      identifierType: 'function',
      name: 'GAUSS',
      exampleUsage: 'GAUSS(2)',
      exampleResult: '0.4772498680518208',
    },
    {
      identifierType: 'function',
      name: 'GEOMEAN',
      exampleUsage: 'GEOMEAN([2,4], [8,16])',
      exampleResult: '5.656854249492381',
    },
    {
      identifierType: 'function',
      name: 'GROWTH',
      exampleUsage: 'GROWTH([2,4,8,16], [1,2,3,4], [5])',
      exampleResult: '32.00000000000003',
    },
    {
      identifierType: 'function',
      name: 'HARMEAN',
      exampleUsage: 'HARMEAN([2,4], [8,16])',
      exampleResult: '4.266666666666667',
    },
    {
      identifierType: 'function',
      name: 'HYPGEOMDIST',
      exampleUsage: 'HYPGEOMDIST(1, 4, 8, 20, false)',
      exampleResult: '0.3632610939112487',
    },
    {
      identifierType: 'function',
      name: 'INTERCEPT',
      exampleUsage: 'INTERCEPT([2,3,9,1,8], [6,5,11,7,5])',
      exampleResult: '0.04838709677419217',
    },
    {
      identifierType: 'function',
      name: 'KURT',
      exampleUsage: 'KURT([3,4,5,2,3,4,5,6,4,7])',
      exampleResult: '-0.15179963720841627',
    },
    {
      identifierType: 'function',
      name: 'LARGE',
      exampleUsage: 'LARGE([3,5,3,5,4,4,2,4,6,7], 3)',
      exampleResult: '5',
    },
    {
      identifierType: 'function',
      name: 'LINEST',
      exampleUsage: 'LINEST([1,9,5,7], [0,4,2,3], true, true)',
      exampleResult: '2,1',
    },
    {
      identifierType: 'function',
      name: 'LOGNORMDIST',
      exampleUsage: 'LOGNORMDIST(4, 3.5, 1.2, true)',
      exampleResult: '0.0390835557068005',
    },
    {
      identifierType: 'function',
      name: 'LOGNORMINV',
      exampleUsage: 'LOGNORMINV(0.0390835557068005, 3.5, 1.2, true)',
      exampleResult: '4.000000000000001',
    },
    {
      identifierType: 'function',
      name: 'MAX',
      exampleUsage: 'MAX([0.1,0.2], [0.4,0.8], [true, false])',
      exampleResult: '0.8',
    },
    {
      identifierType: 'function',
      name: 'MAXA',
      exampleUsage: 'MAXA([0.1,0.2], [0.4,0.8], [true, false])',
      exampleResult: '1',
    },
    {
      identifierType: 'function',
      name: 'MEDIAN',
      exampleUsage: 'MEDIAN([1,2,3], [4,5,6])',
      exampleResult: '3.5',
    },
    {
      identifierType: 'function',
      name: 'MIN',
      exampleUsage: 'MIN([0.1,0.2], [0.4,0.8], [true, false])',
      exampleResult: '0.1',
    },
    {
      identifierType: 'function',
      name: 'MINA',
      exampleUsage: 'MINA([0.1,0.2], [0.4,0.8], [true, false])',
      exampleResult: '0',
    },
    {
      identifierType: 'function',
      name: 'MODEMULT',
      exampleUsage: 'MODEMULT([1,2,3,4,3,2,1,2,3])',
      exampleResult: '2,3',
    },
    {
      identifierType: 'function',
      name: 'MODESNGL',
      exampleUsage: 'MODESNGL([1,2,3,4,3,2,1,2,3])',
      exampleResult: '2',
    },
    {
      identifierType: 'function',
      name: 'NORMDIST',
      exampleUsage: 'NORMDIST(42, 40, 1.5, true)',
      exampleResult: '0.9087887802741321',
    },
    {
      identifierType: 'function',
      name: 'NORMINV',
      exampleUsage: 'NORMINV(0.9087887802741321, 40, 1.5)',
      exampleResult: '42',
    },
    {
      identifierType: 'function',
      name: 'NORMSDIST',
      exampleUsage: 'NORMSDIST(1, true)',
      exampleResult: '0.8413447460685429',
    },
    {
      identifierType: 'function',
      name: 'NORMSINV',
      exampleUsage: 'NORMSINV(0.8413447460685429)',
      exampleResult: '1.0000000000000002',
    },
    {
      identifierType: 'function',
      name: 'PEARSON',
      exampleUsage: 'PEARSON([9,7,5,3,1], [10,6,1,5,3])',
      exampleResult: '0.6993786061802354',
    },
    {
      identifierType: 'function',
      name: 'PERCENTILEEXC',
      exampleUsage: 'PERCENTILEEXC([1,2,3,4], 0.3)',
      exampleResult: '1.5',
    },
    {
      identifierType: 'function',
      name: 'PERCENTILEINC',
      exampleUsage: 'PERCENTILEINC([1,2,3,4], 0.3)',
      exampleResult: '1.9',
    },
    {
      identifierType: 'function',
      name: 'PERCENTRANKEXC',
      exampleUsage: 'PERCENTRANKEXC([1,2,3,4], 2, 2)',
      exampleResult: '0.4',
    },
    {
      identifierType: 'function',
      name: 'PERCENTRANKINC',
      exampleUsage: 'PERCENTRANKINC([1,2,3,4], 2, 2)',
      exampleResult: '0.33',
    },
    {
      identifierType: 'function',
      name: 'PERMUT',
      exampleUsage: 'PERMUT(100, 3)',
      exampleResult: '970200',
    },
    {
      identifierType: 'function',
      name: 'PERMUTATIONA',
      exampleUsage: 'PERMUTATIONA(4, 3)',
      exampleResult: '64',
    },
    {
      identifierType: 'function',
      name: 'PHI',
      exampleUsage: 'PHI(0.75)',
      exampleResult: '0.30113743215480443',
    },
    {
      identifierType: 'function',
      name: 'POISSONDIST',
      exampleUsage: 'POISSONDIST(2, 5, true)',
      exampleResult: '0.12465201948308113',
    },
    {
      identifierType: 'function',
      name: 'PROB',
      exampleUsage: 'PROB([1,2,3,4], [0.1,0.2,0.2,0.1], 2, 3)',
      exampleResult: '0.4',
    },
    {
      identifierType: 'function',
      name: 'QUARTILEEXC',
      exampleUsage: 'QUARTILEEXC([1,2,3,4], 1)',
      exampleResult: '1.25',
    },
    {
      identifierType: 'function',
      name: 'QUARTILEINC',
      exampleUsage: 'QUARTILEINC([1,2,3,4], 1)',
      exampleResult: '1.75',
    },
    {
      identifierType: 'function',
      name: 'RANKAVG',
      exampleUsage: 'RANKAVG(4, [2,4,4,8,8,16], false)',
      exampleResult: '4.5',
    },
    {
      identifierType: 'function',
      name: 'RANKEQ',
      exampleUsage: 'RANKEQ(4, [2,4,4,8,8,16], false)',
      exampleResult: '4',
    },
    {
      identifierType: 'function',
      name: 'RSQ',
      exampleUsage: 'RSQ([9,7,5,3,1], [10,6,1,5,3])',
      exampleResult: '0.4891304347826088',
    },
    {
      identifierType: 'function',
      name: 'SKEW',
      exampleUsage: 'SKEW([3,4,5,2,3,4,5,6,4,7])',
      exampleResult: '0.3595430714067974',
    },
    {
      identifierType: 'function',
      name: 'SKEWP',
      exampleUsage: 'SKEWP([3,4,5,2,3,4,5,6,4,7])',
      exampleResult: '0.303193339354144',
    },
    {
      identifierType: 'function',
      name: 'SLOPE',
      exampleUsage: 'SLOPE([1,9,5,7], [0,4,2,3])',
      exampleResult: '2',
    },
    {
      identifierType: 'function',
      name: 'SMALL',
      exampleUsage: 'SMALL([3,5,3,5,4,4,2,4,6,7], 3)',
      exampleResult: '3',
    },
    {
      identifierType: 'function',
      name: 'STANDARDIZE',
      exampleUsage: 'STANDARDIZE(42, 40, 1.5)',
      exampleResult: '1.3333333333333333',
    },
    {
      identifierType: 'function',
      name: 'STDEVA',
      exampleUsage: 'STDEVA([2,4], [8,16], [true, false])',
      exampleResult: '6.013872850889572',
    },
    {
      identifierType: 'function',
      name: 'STDEVP',
      exampleUsage: 'STDEVP([2,4], [8,16], [true, false])',
      exampleResult: '5.361902647381804',
    },
    {
      identifierType: 'function',
      name: 'STDEVPA',
      exampleUsage: 'STDEVPA([2,4], [8,16], [true, false])',
      exampleResult: '5.489889697333535',
    },
    {
      identifierType: 'function',
      name: 'STDEVS',
      exampleUsage: 'STDEVS([2,4], [8,16], [true, false])',
      exampleResult: '6.191391873668904',
    },
    {
      identifierType: 'function',
      name: 'STEYX',
      exampleUsage: 'STEYX([2,3,9,1,8,7,5], [6,5,11,7,5,4,4])',
      exampleResult: '3.305718950210041',
    },
    {
      identifierType: 'function',
      name: 'TDIST',
      exampleUsage: 'TDIST(60, 1, true)',
      exampleResult: '0.9946953263673741',
    },
    {
      identifierType: 'function',
      name: 'TINV',
      exampleUsage: 'TINV(0.9946953263673741, 1)',
      exampleResult: '59.99999999996535',
    },
    {
      identifierType: 'function',
      name: 'TRIMMEAN',
      exampleUsage: 'TRIMMEAN([4,5,6,7,2,3,4,5,1,2,3], 0.2)',
      exampleResult: '3.7777777777777777',
    },
    {
      identifierType: 'function',
      name: 'VARA',
      exampleUsage: 'VARA([2,4], [8,16], [true, false])',
      exampleResult: '36.16666666666667',
    },
    {
      identifierType: 'function',
      name: 'VARP',
      exampleUsage: 'VARP([2,4], [8,16], [true, false])',
      exampleResult: '28.75',
    },
    {
      identifierType: 'function',
      name: 'VARPA',
      exampleUsage: 'VARPA([2,4], [8,16], [true, false])',
      exampleResult: '30.13888888888889',
    },
    {
      identifierType: 'function',
      name: 'VARS',
      exampleUsage: 'VARS([2,4], [8,16], [true, false])',
      exampleResult: '38.333333333333336',
    },
    {
      identifierType: 'function',
      name: 'WEIBULLDIST',
      exampleUsage: 'WEIBULLDIST(105, 20, 100, true)',
      exampleResult: '0.9295813900692769',
    },
    {
      identifierType: 'function',
      name: 'ZTEST',
      exampleUsage: 'ZTEST([3,6,7,8,6,5,4,2,1,9], 4)',
      exampleResult: '0.09057419685136381',
    },
    {
      identifierType: 'function',
      name: 'CHAR',
      exampleUsage: 'CHAR(65)',
      exampleResult: 'A',
    },
    {
      identifierType: 'function',
      name: 'CLEAN',
      exampleUsage: "CLEAN('Monthly report')",
      exampleResult: 'Monthly report',
    },
    {
      identifierType: 'function',
      name: 'CODE',
      exampleUsage: "CODE('A')",
      exampleResult: '65',
    },
    {
      identifierType: 'function',
      name: 'CONCATENATE',
      exampleUsage: "CONCATENATE('Andreas', ' ', 'Hauser')",
      exampleResult: 'Andreas Hauser',
    },
    {
      identifierType: 'function',
      name: 'EXACT',
      exampleUsage: "EXACT('Word', 'word')",
      exampleResult: 'false',
    },
    {
      identifierType: 'function',
      name: 'FIND',
      exampleUsage: "FIND('M', 'Miriam McGovern', 3)",
      exampleResult: '8',
    },
    {
      identifierType: 'function',
      name: 'LEFT',
      exampleUsage: "LEFT('Sale Price', 4)",
      exampleResult: 'Sale',
    },
    {
      identifierType: 'function',
      name: 'LEN',
      exampleUsage: "LEN('Phoenix, AZ')",
      exampleResult: '11',
    },
    {
      identifierType: 'function',
      name: 'LOWER',
      exampleUsage: "LOWER('E. E. Cummings')",
      exampleResult: 'e. e. cummings',
    },
    {
      identifierType: 'function',
      name: 'MID',
      exampleUsage: "MID('Fluid Flow', 7, 20)",
      exampleResult: 'Flow',
    },
    {
      identifierType: 'function',
      name: 'NUMBERVALUE',
      exampleUsage: "NUMBERVALUE('2.500,27', ',', '.')",
      exampleResult: '2500.27',
    },
    {
      identifierType: 'function',
      name: 'PROPER',
      exampleUsage: "PROPER('this is a TITLE')",
      exampleResult: 'This Is A Title',
    },
    {
      identifierType: 'function',
      name: 'REGEXEXTRACT',
      exampleUsage: "REGEXEXTRACT('Palo Alto', 'Alto')",
      exampleResult: 'Alto',
    },
    {
      identifierType: 'function',
      name: 'REGEXMATCH',
      exampleUsage: "REGEXMATCH('Palo Alto', 'Alto')",
      exampleResult: 'true',
    },
    {
      identifierType: 'function',
      name: 'REGEXREPLACE',
      exampleUsage: "REGEXREPLACE('Sutoiku', 'utoiku', 'TOIC')",
      exampleResult: 'STOIC',
    },
    {
      identifierType: 'function',
      name: 'REPLACE',
      exampleUsage: "REPLACE('abcdefghijk', 6, 5, '*')",
      exampleResult: 'abcde*k',
    },
    {
      identifierType: 'function',
      name: 'REPT',
      exampleUsage: "REPT('*-', 3)",
      exampleResult: '*-*-*-',
    },
    {
      identifierType: 'function',
      name: 'RIGHT',
      exampleUsage: "RIGHT('Sale Price', 5)",
      exampleResult: 'Price',
    },
    {
      identifierType: 'function',
      name: 'ROMAN',
      exampleUsage: 'ROMAN(499)',
      exampleResult: 'CDXCIX',
    },
    {
      identifierType: 'function',
      name: 'SEARCH',
      exampleUsage: "SEARCH('margin', 'Profit Margin')",
      exampleResult: '8',
    },
    {
      identifierType: 'function',
      name: 'SPLIT',
      exampleUsage: "SPLIT('A,B,C', ',')",
      exampleResult: 'A,B,C',
    },
    {
      identifierType: 'function',
      name: 'SUBSTITUTE',
      exampleUsage: "SUBSTITUTE('Quarter 1, 2011', '1', '2', 3)",
      exampleResult: 'Quarter 1, 2012',
    },
    {
      identifierType: 'function',
      name: 'T',
      exampleUsage: "T('Rainfall')",
      exampleResult: 'Rainfall',
    },
    {
      identifierType: 'function',
      name: 'TRIM',
      exampleUsage: "TRIM(' First Quarter Earnings ')",
      exampleResult: 'First Quarter Earnings',
    },
    {
      identifierType: 'function',
      name: 'UNICHAR',
      exampleUsage: 'UNICHAR(66)',
      exampleResult: 'B',
    },
    {
      identifierType: 'function',
      name: 'UNICODE',
      exampleUsage: "UNICODE('B')",
      exampleResult: '66',
    },
    {
      identifierType: 'function',
      name: 'UPPER',
      exampleUsage: "UPPER('total')",
      exampleResult: 'TOTAL',
    },
  ];
  const availableFunctionsMap = new Map<
    CalculatedIdentifierDef['name'],
    CalculatedIdentifierDef
  >(availableFunctions.map((f) => [f.name, f]));

  /** return suggestions based on the end of the text */
  const getSuggestions = (text: string): CalculatedIdentifierDef[] => {
    // get the last word (delimited by space, comma, open parenthesis, operators (+, -, *, /))
    const lastWord = text
      .split(/[\s,+\-*/(]/)
      .pop()
      ?.trim();
    if (!lastWord?.length) return [];

    if (lastWord.startsWith(VARIABLE_WRAPPER.OPEN)) {
      const lookupStr = (() => {
        let str = lastWord.toLowerCase();
        str = str.slice(VARIABLE_WRAPPER.OPEN.length); // remove the open wrapper
        return str;
      })();

      // do a variable lookup
      return availableVariables.value.filter(
        (v) =>
          v.name.toLowerCase().startsWith(lookupStr) &&
          v.name.length !== lookupStr.length // don't suggest the same word
      );
    } else {
      // do a function lookup
      const availableFormulas = availableFunctions.filter(
        (f) =>
          f.name.toUpperCase().startsWith(lastWord.toUpperCase()) &&
          f.name.length !== lastWord.length // don't suggest the same word
      );
      return availableFormulas;
    }
  };

  const insertIdentifierAtPos = (
    identifier: CalculatedIdentifierDef,
    textToManip: string,
    pos: number
  ) => {
    const textToInsert = (() => {
      if (identifier.identifierType === 'variable')
        return VARIABLE_WRAPPER.OPEN + identifier.name + VARIABLE_WRAPPER.CLOSE;
      return identifier.exampleUsage || identifier.name;
    })();

    let prependText = textToManip.slice(0, pos);
    // incorporate the text seamlessly if prependText ends with the beginning of the textToInsert
    if (prependText.length) {
      const lastWord = prependText.split(/[\s,+\-*/(]/).pop();
      if (lastWord && textToInsert.startsWith(lastWord)) {
        prependText = prependText.slice(0, -lastWord.length); // remove the last word so insertion is seamless
      }
    }
    const appendText = textToManip.slice(pos);

    const shouldPrependSpace = (() => {
      if (!prependText.length) return false;
      const lastChar = prependText[prependText.length - 1];
      return lastChar !== '(' && lastChar !== ' ';
    })();
    const shouldAppendSpace = (() => {
      if (!appendText.length) return false;
      const firstChar = appendText[0];
      return firstChar !== ')' && firstChar !== ' ';
    })();

    const fullTextToInsert = [
      shouldPrependSpace ? ' ' : '',
      textToInsert,
      shouldAppendSpace ? ' ' : '',
    ].join('');
    const ret = [prependText, fullTextToInsert, appendText].join('');

    return {
      text: ret,
      textToInsert: fullTextToInsert,
    };
  };

  /* ------------------------- syntax / parsing ------------------------- */

  const parser = generateParser(grammar);

  const parseSyntax = (text: string) => {
    try {
      const expr: VExpression = parser.parse(text);

      const variablesUsed = new Set<CalculatedIdentifierDef['name']>();
      walkExpression(expr, (val) => {
        if (val.type === 'variable') {
          variablesUsed.add(val.value);
        }
      });
      const functionsUsed = new Set<CalculatedIdentifierDef['name']>();
      walkExpression(expr, (val) => {
        if (val.type === 'function-call') {
          functionsUsed.add(val.value);
        }
      });

      // to valid JavaScript (by replacing VARIABLE_WRAPPER with empty string)
      const runnableCode = (() =>
        replaceVariables(text, 'preset:javascript'))();

      return {
        runnableCode,
        parsed: {
          expr,
          variableDependencies: [...variablesUsed].map((v) => {
            return {
              name: v,
              variable: availableVariablesMap.value.get(v),
            };
          }),
          functionDependencies: [...functionsUsed].map((f) => {
            return {
              name: f,
              function: availableFunctionsMap.get(f),
            };
          }),
          message: 'Syntax is valid',
        },
        isValid: true,
      };
    } catch (e) {
      const message = get(e, 'message', 'Syntax is invalid');
      return {
        runnableCode: '',
        parsed: {
          expr: null,
          variableDependencies: [],
          functionDependencies: [],
          message,
        },
        isValid: false,
      };
    }
  };

  return {
    availableVariables,
    availableVariablesMap,
    availableFunctions,
    availableFunctionsMap,
    getSuggestions,
    insertIdentifierAtPos,
    parseSyntax,
  };
};

export const safeEval = function (
  code: string,
  /** variables to expose to the code */
  context: Record<string, unknown>
) {
  const noGlobals = {
    window: undefined,
    document: undefined,
    navigator: undefined,
  };
  let result: unknown;
  const start = Date.now();

  let isError = false;
  try {
    const fn = new Function(
      ...Object.keys(noGlobals),
      ...Object.keys(context),
      `
    "use strict";
    return (${code});
    `
    ).bind(undefined);
    result = fn(...Object.values(noGlobals), ...Object.values(context));
  } catch (e) {
    // Error occurred during evaluation
    isError = true;
    result = get(e, 'message', 'Error occurred during evaluation');
  }
  const end = Date.now();
  return { result, isError, time: end - start };
};
